import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Ticket, TrendingUp, Trophy, ShieldCheck, Banknote, Gavel, Search, History, LifeBuoy, AlertTriangle, LineChart, Trash2, RotateCcw } from "lucide-react";
import { TEAMS } from "../lib/site";
import { adminApi } from "../lib/api";
import { inr, timeFrom } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import AdminUsers from "../components/AdminUsers";
import AdminChat from "../components/AdminChat";
import AdminOdds from "../components/AdminOdds";
import AdminToss from "../components/AdminToss";
import AdminOtherSlips from "../components/AdminOtherSlips";
import AdminCasinoStatus from "../components/AdminCasinoStatus";
import AdminManualPayMethods from "../components/AdminManualPayMethods";

const BET_KINDS = [
  { id: "winner", label: "Match Odds" },
  { id: "session", label: "Session" },
  { id: "lambi", label: "Lambi" },
  { id: "toss", label: "Toss" },
];

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <Icon className="h-5 w-5 text-primary" />
      <div className="mt-3 font-display font-extrabold text-2xl">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

const statusBadge = (s) =>
  s === "live" ? "border-red-500/40 text-red-400"
    : s === "upcoming" ? "border-primary/40 text-primary"
    : "text-muted-foreground";

const AUDIT_META = {
  deposit_upi: { label: "UPI Deposit", cls: "border-primary/40 text-primary" },
  deposit_bank: { label: "Bank Deposit", cls: "border-primary/40 text-primary" },
  deposit_bank_rejected: { label: "Bank Deposit Rejected", cls: "border-red-500/40 text-red-400" },
  withdrawal_request: { label: "Withdrawal Requested", cls: "border-amber-500/40 text-amber-400" },
  withdrawal_approved: { label: "Withdrawal Approved", cls: "border-red-500/40 text-red-400" },
  withdrawal_rejected: { label: "Withdrawal Rejected", cls: "border-sky-500/40 text-sky-400" },
  chip_adjust: { label: "Chip Adjust", cls: "border-fuchsia-500/40 text-fuchsia-400" },
  bet_cancel: { label: "Bet Cancelled", cls: "border-red-500/40 text-red-400" },
  match_delete: { label: "Match Removed", cls: "border-red-500/40 text-red-400" },
  stats_reset: { label: "Stats Reset", cls: "border-amber-500/40 text-amber-400" },
  user_delete: { label: "User Deleted", cls: "border-red-500/40 text-red-400" },
};
const auditCategory = (event) =>
  event?.startsWith("deposit") ? "deposits"
    : event?.startsWith("withdrawal") ? "withdrawals"
    : event === "chip_adjust" ? "adjust" : "other";

export default function Admin() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [tab, setTab] = useState("matches");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [manualDeposits, setManualDeposits] = useState([]);
  const [upiDeposits, setUpiDeposits] = useState([]);
  const [payoutOn, setPayoutOn] = useState(false);
  const [allBets, setAllBets] = useState([]);
  const [betSearch, setBetSearch] = useState("");
  const [betKind, setBetKind] = useState("winner");
  const [kindCount, setKindCount] = useState(null);
  const [slipsRefresh, setSlipsRefresh] = useState(0);
  const [mdSearch, setMdSearch] = useState("");
  const [mdStatus, setMdStatus] = useState("all");
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditCat, setAuditCat] = useState("all");
  const [tickets, setTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState("pending");
  const [houseReport, setHouseReport] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [bank, setBank] = useState({ accountName: "", accountNumber: "", ifsc: "", bankName: "" });
  const [bankSaving, setBankSaving] = useState(false);
  const [settlePick, setSettlePick] = useState({}); // matchId -> selectionLabel

  const loadAll = async (tk) => {
    const [s, u, m, w, md, al, bd, bt, tks] = await Promise.all([
      adminApi.stats(tk), adminApi.users(tk), adminApi.matches(), adminApi.withdrawals(tk), adminApi.manualDeposits(tk), adminApi.auditLogs(tk), adminApi.getBankDetails(tk), adminApi.bets(tk), adminApi.tickets(tk),
    ]);
    setStats(s); setUsers(u); setMatches(m); setWithdrawals(w); setManualDeposits(md); setAuditLogs(al); setAllBets(bt); setTickets(tks);
    adminApi.houseReport(tk).then(setHouseReport).catch(() => {});
    adminApi.upiDeposits(tk).then((r) => setUpiDeposits(Array.isArray(r) ? r : [])).catch(() => {});
    adminApi.payoutConfig(tk).then((r) => setPayoutOn(!!r?.enabled)).catch(() => {});
    adminApi.affiliates(tk).then((r) => setAffiliates(r.affiliates || [])).catch(() => {});
    if (bd?.bank) setBank({ accountName: bd.bank.accountName || "", accountNumber: bd.bank.accountNumber || "", ifsc: bd.bank.ifsc || "", bankName: bd.bank.bankName || "" });
  };

  const saveBank = async () => {
    setBankSaving(true);
    try {
      const res = await adminApi.setBankDetails(token, bank);
      if (res?.bank) setBank({ accountName: res.bank.accountName, accountNumber: res.bank.accountNumber, ifsc: res.bank.ifsc, bankName: res.bank.bankName || "" });
      toast({ title: "Bank details saved \u2705", description: "Users now see the updated deposit account." });
    } catch (e) {
      toast({ title: "Could not save", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setBankSaving(false);
    }
  };

  const doLogin = async () => {
    let tk;
    try {
      const res = await adminApi.login({ email: email.trim(), password: pwd.trim() });
      tk = res.token;
    } catch (e) {
      toast({ title: "Login failed", description: e?.response?.data?.detail || "Check username & password." });
      return;
    }
    // Login succeeded — enter the panel immediately, then load data (don't block login on data errors)
    setToken(tk);
    try {
      await loadAll(tk);
    } catch {
      toast({ title: "Signed in", description: "Some dashboard data failed to load — retrying will fix it." });
    }
  };

  const refresh = () => { setSlipsRefresh((n) => n + 1); return token && loadAll(token); };

  const changeStatus = async (matchId, status) => {
    try {
      await adminApi.setStatus(token, matchId, status);
      toast({ title: "Match status updated \u2705", description: status });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  const toggleSuspend = async (match) => {
    const next = !match.bettingSuspended;
    try {
      await adminApi.setSuspend(token, match.id, next);
      toast({ title: next ? "Betting suspended \u26d4" : "Betting resumed \u2705", description: next ? "Users can't place new bets on this match." : "Users can bet again." });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  const toggleSessionSuspend = async (match) => {
    const next = !match.sessionSuspended;
    try {
      await adminApi.setSessionSuspend(token, match.id, next);
      toast({ title: next ? "Session/Lambi killed \u26d4" : "Session/Lambi resumed \u2705", description: next ? "No new session or lambi bets on this match (match-winner unaffected)." : "Session & Lambi betting is open again." });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  const payAffiliate = async (a) => {
    if (!window.confirm(`Pay ${inr(a.pending)} commission to ${a.name || a.username}? This credits their wallet.`)) return;
    try {
      const res = await adminApi.payAffiliate(token, a.id);
      toast({ title: "Commission paid \u2705", description: `${inr(res.paid)} credited to ${a.name || a.username}.` });
      refresh();
    } catch (e) {
      toast({ title: "Could not pay", description: e?.response?.data?.detail || "Try again." });
    }
  };


  const resetHouse = async () => {
    if (!window.confirm("Reset the House P&L report to zero from now? This only moves the reporting start point — no balances, bets or history are touched.")) return;
    try {
      await adminApi.resetHouse(token);
      toast({ title: "House P&L reset \u2705", description: "House P&L now counts from now." });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || "Try again." });
    }
  };


  const resetStats = async () => {
    if (!window.confirm("Reset the Turnover & Winnings-paid counters to zero? This only affects these two dashboard numbers — no balances, bets or history are touched.")) return;
    try {
      await adminApi.resetStats(token);
      toast({ title: "Counters reset \u2705", description: "Turnover & winnings-paid now count from now." });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail || "Try again." });
    }
  };


  const removeMatch = async (match) => {
    const name = teamsOf(match) || match.id;
    if (!window.confirm(`Remove "${name}" from the panel? Any still-open bets on it will be VOIDED and refunded. Use this to clear a match that is stuck or already settled.`)) return;
    try {
      const res = await adminApi.deleteMatch(token, match.id);
      toast({ title: "Match removed \u2705", description: res.voidedBets > 0 ? `${res.voidedBets} open bet(s) voided & refunded to ${res.refundedUsers} user(s).` : "Cleared from the panel." });
      refresh();
    } catch (e) {
      toast({ title: "Could not remove", description: e?.response?.data?.detail || "Try again." });
    }
  };

  const cancelBet = async (bet) => {
    if (!window.confirm(`Cancel ${bet.ref}? This refunds the user's locked exposure and voids the bet. Use only if the odds were wrong.`)) return;
    try {
      const res = await adminApi.cancelBet(token, bet.id);
      toast({ title: "Bet cancelled \u2705", description: `${bet.ref} \u2014 ${inr(Math.max(0, Math.round(res.released || 0)))} released to ${bet.username || "user"}.` });
      refresh();
    } catch (e) {
      toast({ title: "Could not cancel", description: e?.response?.data?.detail || "Try again." });
    }
  };

  const settle = async (match, winnerOverride) => {
    const winner = winnerOverride || settlePick[match.id];
    if (!winner) { toast({ title: "Pick the winning selection first" }); return; }
    try {
      const res = await adminApi.settle(token, match.id, "Match Winner", winner);
      toast({ title: "Match settled \ud83c\udfc6", description: `${res.won} won, ${res.lost} lost, payouts credited.` });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  const wdPayout = async (id, recheck = false) => {
    try {
      const r = recheck ? await adminApi.payoutRecheck(token, id) : await adminApi.payout(token, id);
      const msg = r.status === "paid" ? `Paid · UTR ${r.utr || "-"}` : r.status === "rejected" ? "Bank payout failed — amount refunded to user." : "Sent to IndianPay — bank transfer in progress.";
      toast({ title: recheck ? "Payout status" : "Payout started", description: msg });
      refresh();
    } catch (e) {
      toast({ title: "Payout failed", description: e?.response?.data?.detail });
    }
  };

  const wdAction = async (id, action) => {
    try {
      await adminApi.withdrawalAction(token, id, action);
      const desc = action === "reject" ? "Amount refunded to user." : "Marked as paid — user sees it as Credited.";
      toast({ title: `Withdrawal ${action === "approve" ? "approved" : "rejected"}`, description: desc });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  const mdAction = async (id, action) => {
    try {
      await adminApi.manualDepositAction(token, id, action);
      toast({ title: `Deposit ${action === "approve" ? "approved" : "rejected"}`, description: action === "approve" ? "Amount + 5% bonus credited to user." : "Request rejected." });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  const upiRecheck = async (id) => {
    try {
      const d = await adminApi.upiRecheck(token, id);
      toast({ title: `UPI order ${d.status}`, description: d.status === "success" ? "Verified with gateway and credited." : d.remark || "No change from gateway." });
      refresh();
    } catch (e) {
      toast({ title: "Re-check failed", description: e?.response?.data?.detail });
    }
  };

  const resolveTicket = async (id) => {
    try {
      await adminApi.resolveTicket(token, id);
      toast({ title: "Ticket resolved & closed ✅", description: "User notified in chat; ticket removed." });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  const [ticketNote, setTicketNote] = useState({}); // ticketId -> draft comment
  const commentTicket = async (id) => {
    const text = (ticketNote[id] || "").trim();
    if (!text) return;
    try {
      await adminApi.commentTicket(token, id, text);
      setTicketNote((p) => ({ ...p, [id]: "" }));
      toast({ title: "Comment sent", description: "Delivered to the user's chat." });
      refresh();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail });
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 py-20">
        <div className="rounded-2xl border border-border bg-card p-8">
          <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center ring-1 ring-primary/30 mb-4">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <h1 className="font-display font-bold text-2xl">Admin Panel</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to manage the platform.</p>
          <div className="space-y-3 mt-6">
            <div><Label>Username</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter admin username" className="mt-1.5" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="off" name="admin_user_field" /></div>
            <div><Label>Password</Label><Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Enter password" className="mt-1.5" autoComplete="new-password" name="admin_pass_field" onKeyDown={(e) => { if (e.key === "Enter") doLogin(); }} /></div>
          </div>
          <Button className="w-full mt-5 font-semibold" onClick={doLogin}>Login to Admin</Button>
        </div>
      </div>
    );
  }

  const pendingWd = withdrawals.filter((w) => w.status === "pending").length;
  const pendingMd = manualDeposits.filter((d) => d.status === "pending").length;
  const pendingTickets = tickets.filter((t) => t.status === "pending").length;
  const filteredTickets = tickets.filter((t) => ticketFilter === "all" || t.status === ticketFilter);

  // Stuck-match watchdog: flag matches that are unsettled beyond an expected window.
  const nowMs = Date.now();
  const ageMin = (iso) => (iso ? (nowMs - new Date(iso).getTime()) / 60000 : Infinity);
  const STALE_LIVE_MIN = 15;   // a "live" match with no sync update this long is likely finished but undetected
  const PENDING_MIN = 10;      // a queued match awaiting admin approval this long needs attention
  const staleLive = matches.filter((m) => m.status === "live" && ageMin(m.updatedAt) > STALE_LIVE_MIN);
  const stuckPending = matches.filter((m) => m.settlementStatus === "pending" && ageMin(m.settlementQueuedAt || m.updatedAt) > PENDING_MIN);
  const watchdog = [
    ...stuckPending.map((m) => ({ m, kind: "pending" })),
    ...staleLive.map((m) => ({ m, kind: "stale" })),
  ];
  const teamsOf = (m) => `${m.homeTeam?.name || TEAMS[m.home]?.name || m.home} vs ${m.awayTeam?.name || TEAMS[m.away]?.name || m.away}`;
  const mdCounts = {
    all: manualDeposits.length,
    pending: pendingMd,
    approved: manualDeposits.filter((d) => d.status === "approved").length,
    rejected: manualDeposits.filter((d) => d.status === "rejected").length,
  };
  const mdQuery = mdSearch.trim().toLowerCase();
  const filteredMd = manualDeposits.filter((d) => {
    if (mdStatus !== "all" && d.status !== mdStatus) return false;
    if (!mdQuery) return true;
    return String(d.utr || "").toLowerCase().includes(mdQuery) ||
      String(d.username || "").toLowerCase().includes(mdQuery);
  });

  const filteredAudit = auditLogs.filter((a) => auditCat === "all" || auditCategory(a.event) === auditCat);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-bold text-3xl">Admin Panel</h1>
        <Button variant="outline" className="bg-white/5 border-white/15" onClick={() => { setToken(null); navigate("/?auth=login", { replace: true }); }} data-testid="admin-logout-btn">Logout</Button>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground">
          {stats?.statsResetAt
            ? <>Turnover & winnings-paid counted since <span className="text-foreground font-medium">{new Date(stats.statsResetAt).toLocaleString("en-IN")}</span></>
            : "Turnover & winnings-paid counted since launch (all-time)"}
        </div>
        <Button size="sm" variant="outline" data-testid="admin-reset-stats-btn" className="bg-white/5 border-white/15 gap-1.5 text-amber-300" onClick={resetStats}>
          <RotateCcw className="h-4 w-4" /> Reset counters
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard icon={Users} label="Total users" value={(stats?.totalUsers ?? 0).toLocaleString("en-IN")} />
        <StatCard icon={Ticket} label="Total bets" value={(stats?.totalBets ?? 0).toLocaleString("en-IN")} />
        <StatCard icon={TrendingUp} label="Turnover" value={inr(stats?.totalTurnover ?? 0)} />
        <StatCard icon={Trophy} label="Winnings paid" value={inr(stats?.totalWinnings ?? 0)} />
      </div>

      {watchdog.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-500/50 bg-amber-500/10 p-4 sm:p-5" data-testid="admin-watchdog">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 shrink-0 rounded-xl bg-amber-500/20 grid place-items-center ring-1 ring-amber-500/40">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-display font-semibold text-amber-300" data-testid="admin-watchdog-title">
                  {watchdog.length} match{watchdog.length > 1 ? "es" : ""} need attention
                </h3>
                <Button size="sm" className="font-semibold" onClick={() => setTab("settle")} data-testid="admin-watchdog-goto-settle">Go to Settle</Button>
              </div>
              <ul className="mt-2 space-y-1.5">
                {watchdog.map(({ m, kind }) => (
                  <li key={`${kind}-${m.id}`} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`admin-watchdog-row-${m.id}`}>
                    <Badge variant="outline" className={kind === "pending" ? "border-amber-500/50 text-amber-300" : "border-red-500/50 text-red-300"}>
                      {kind === "pending" ? "Awaiting approval" : "May be stuck"}
                    </Badge>
                    <span className="font-medium">{teamsOf(m)}</span>
                    <span className="text-xs text-muted-foreground">
                      {kind === "pending"
                        ? `queued ${timeFrom(m.settlementQueuedAt || m.updatedAt)}${m.suggestedWinner ? ` · suggested: ${m.suggestedWinner}` : ""}`
                        : `no live update ${timeFrom(m.updatedAt)} — result may be in`}
                    </span>
                    <button
                      onClick={() => removeMatch(m)}
                      data-testid={`admin-watchdog-remove-${m.id}`}
                      className="ml-auto inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-[11px] font-semibold text-red-300 hover:bg-red-500/20 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">Auto-settle queues finished matches within ~15s. If a match lingers here, confirm the winner manually in the Settle tab.</p>
            </div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-card border border-border flex-wrap h-auto">
          <TabsTrigger value="matches" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Matches</TabsTrigger>
          <TabsTrigger value="odds" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Odds</TabsTrigger>
          <TabsTrigger value="settle" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Settle Bets</TabsTrigger>
          <TabsTrigger value="toss" data-testid="admin-toss-tab" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Toss Settlement</TabsTrigger>
          <TabsTrigger value="betslips" data-testid="admin-betslips-tab" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Bet Slips</TabsTrigger>
          <TabsTrigger value="withdrawals" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Withdrawals {pendingWd > 0 && <span className="ml-1.5 rounded-full bg-amber-500 text-black text-[10px] px-1.5">{pendingWd}</span>}
          </TabsTrigger>
          <TabsTrigger value="deposits" data-testid="admin-deposits-tab" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Deposits {pendingMd > 0 && <span className="ml-1.5 rounded-full bg-amber-500 text-black text-[10px] px-1.5">{pendingMd}</span>}
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Users</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-audit-tab" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Audit Log</TabsTrigger>
          <TabsTrigger value="support" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Support</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="admin-tickets-tab" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Tickets {pendingTickets > 0 && <span className="ml-1.5 rounded-full bg-amber-500 text-black text-[10px] px-1.5">{pendingTickets}</span>}
          </TabsTrigger>
          <TabsTrigger value="house" data-testid="admin-house-tab" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">House P&amp;L</TabsTrigger>
          <TabsTrigger value="affiliates" data-testid="admin-affiliates-tab" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Affiliates</TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Settings</TabsTrigger>
        </TabsList>

        {/* Matches */}
        <TabsContent value="matches" className="mt-5">
          <h3 className="font-display font-semibold mb-4">Manage Matches — set status</h3>
          <div className="rounded-2xl border border-border bg-card divide-y divide-border">
            {matches.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-5 py-3.5 gap-4">
                <div className="min-w-0">
                  <div className="font-medium">{m.homeTeam?.name || TEAMS[m.home]?.name || m.home} vs {m.awayTeam?.name || TEAMS[m.away]?.name || m.away}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.series} · {m.format}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={statusBadge(m.status)}>{m.status}</Badge>
                  {m.status !== "completed" && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`admin-suspend-${m.id}`}
                      onClick={() => toggleSuspend(m)}
                      className={m.bettingSuspended
                        ? "bg-amber-500/15 border-amber-500/50 text-amber-300 hover:bg-amber-500/25"
                        : "bg-white/5 border-white/15"}
                    >
                      {m.bettingSuspended ? "Resume betting" : "Suspend betting"}
                    </Button>
                  )}
                  {m.status === "live" && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`admin-session-kill-${m.id}`}
                      onClick={() => toggleSessionSuspend(m)}
                      className={m.sessionSuspended
                        ? "bg-fuchsia-500/15 border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-500/25"
                        : "bg-white/5 border-white/15"}
                    >
                      {m.sessionSuspended ? "Resume session" : "Kill session"}
                    </Button>
                  )}
                  <Select value={m.status} onValueChange={(v) => changeStatus(m.id, v)}>
                    <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`admin-remove-match-${m.id}`}
                    onClick={() => removeMatch(m)}
                    className="bg-red-500/10 border-red-500/40 text-red-300 hover:bg-red-500/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Odds */}
        <TabsContent value="odds" className="mt-5">
          <AdminOdds token={token} matches={matches} onChanged={refresh} />
        </TabsContent>

        <TabsContent value="toss" className="mt-5">
          <AdminToss token={token} />
        </TabsContent>

        {/* Settle */}
        <TabsContent value="settle" className="mt-5">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Gavel className="h-4 w-4 text-primary" /> Settle Match Winner bets</h3>
          <p className="text-xs text-muted-foreground mb-4">Only finished matches with a confirmed Jitex result appear here. Live &amp; upcoming matches are never listed.</p>
          <div className="space-y-3">
            {matches.filter((m) => m.settlementStatus === "pending").map((m) => {
              const winnerMarket = (m.markets || []).find((mk) => mk.name === "Match Winner");
              return (
                <div key={m.id} data-testid={`settle-card-${m.id}`} className="rounded-2xl border p-5 border-amber-500/50 bg-amber-500/5">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="font-medium">{m.homeTeam?.name || TEAMS[m.home]?.name || m.home} vs {m.awayTeam?.name || TEAMS[m.away]?.name || m.away}</div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-[11px] font-bold text-amber-400" data-testid={`settle-pending-${m.id}`}>
                      ⚠ Finished — confirm winner
                    </span>
                  </div>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-[11px] font-bold text-primary" data-testid={`settle-result-tag-${m.id}`}>
                      <Trophy className="h-3.5 w-3.5" /> RESULT: {m.result || m.suggestedWinner}
                    </span>
                    {m.suggestedWinner && (
                      <span className="text-xs text-muted-foreground">Jitex suggests winner: <span className="font-semibold text-foreground">{m.suggestedWinner}</span></span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Select value={settlePick[m.id] || m.suggestedWinner || ""} onValueChange={(v) => setSettlePick({ ...settlePick, [m.id]: v })}>
                      <SelectTrigger className="w-56 h-9" data-testid={`settle-select-${m.id}`}><SelectValue placeholder="Select winner" /></SelectTrigger>
                      <SelectContent>
                        {winnerMarket?.options.map((o) => (
                          <SelectItem key={o.id} value={o.label}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="font-semibold" data-testid={`settle-btn-${m.id}`} onClick={() => settle({ ...m, id: m.id }, settlePick[m.id] || m.suggestedWinner)}>Confirm &amp; Payout</Button>
                  </div>
                </div>
              );
            })}
            {matches.filter((m) => m.settlementStatus === "pending").length === 0 && (
              <div className="rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground">No finished matches awaiting settlement.</div>
            )}
          </div>
        </TabsContent>

        {/* Bet Slips */}
        <TabsContent value="betslips" className="mt-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-semibold flex items-center gap-2"><Ticket className="h-4 w-4 text-primary" /> All bet slips <span className="text-xs text-muted-foreground font-normal" data-testid="admin-betslips-count">({betKind === "winner" ? allBets.length : kindCount ?? "…"})</span></h3>
            <div className="relative w-full sm:w-72">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={betSearch} onChange={(e) => setBetSearch(e.target.value)} placeholder="Search by username, ref or match" className="pl-9" data-testid="admin-betslip-search" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-betkind-tabs">
            {BET_KINDS.map((k) => (
              <button
                key={k.id}
                type="button"
                data-testid={`admin-betkind-${k.id}`}
                onClick={() => { setBetKind(k.id); setKindCount(null); }}
                className={`h-8 px-3.5 rounded-full text-xs font-bold uppercase tracking-wide border transition-[color,background-color,border-color] ${
                  betKind === k.id ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:text-foreground"
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>
          {betKind !== "winner" ? (
            <AdminOtherSlips token={token} kind={betKind} search={betSearch} refreshKey={slipsRefresh} onCount={setKindCount} />
          ) : (
          <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="admin-betslips-list">
            {allBets.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground">No bets placed yet.</div>}
            {allBets
              .filter((b) => {
                const q = betSearch.trim().toLowerCase();
                if (!q) return true;
                return [b.username, b.userName, b.ref, b.matchLabel, b.marketName, b.selectionLabel].some((f) => String(f || "").toLowerCase().includes(q));
              })
              .map((b) => (
                <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={`admin-betslip-${b.ref}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-primary" data-testid={`admin-betslip-user-${b.ref}`}>{b.username || b.userName || "—"}</span>
                      <span className="text-xs text-muted-foreground">· {b.ref}</span>
                      {b.side && (
                        <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${b.side === "lay" ? "bg-rose-500/20 text-rose-300" : "bg-sky-500/20 text-sky-300"}`}>
                          {b.side === "lay" ? "Khai" : "Lagai"}
                        </span>
                      )}
                      <Badge variant="outline" className={
                        b.status === "won" ? "border-primary/40 text-primary"
                          : b.status === "lost" ? "border-red-500/40 text-red-400"
                          : "border-amber-500/40 text-amber-400"
                      }>{b.status}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{b.matchLabel} · {b.marketName} → <span className="text-foreground">{b.selectionLabel}</span> · {timeFrom(b.placedAt)}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-semibold">{b.side === "lay" ? "Liab" : "Stake"} {inr(b.risk ?? b.stake)} @ {b.odds}</div>
                      <div className="text-xs text-muted-foreground">Returns {inr(b.potentialReturn)}</div>
                    </div>
                    {(b.status === "matched" || b.status === "pending") && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`admin-cancel-bet-${b.ref}`}
                        onClick={() => cancelBet(b)}
                        className="bg-red-500/10 border-red-500/40 text-red-400 hover:bg-red-500/20"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              ))}
          </div>
          )}
        </TabsContent>

        {/* Withdrawals */}
        <TabsContent value="withdrawals" className="mt-5">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Banknote className="h-4 w-4 text-primary" /> Withdrawal requests</h3>
          <div className="rounded-2xl border border-border bg-card divide-y divide-border">
            {withdrawals.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground">No withdrawal requests.</div>}
            {withdrawals.map((w) => (
              <div key={w.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <div className="min-w-0">
                  <div className="font-semibold">{inr(w.amount)} <span className="text-xs text-muted-foreground">· {w.userName}</span></div>
                  <div className="text-xs text-muted-foreground">{(w.method === "upi" || w.upiId) ? <>UPI ID <b className="text-foreground select-all">{w.upiId}</b>{w.accountName ? ` · ${w.accountName}` : ""}</> : <>{w.accountName} · A/C {w.accountNumber} · IFSC {w.ifsc}</>} · {timeFrom(w.at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  {w.status === "pending" ? (
                    <>
                      {payoutOn && !(w.method === "upi" || w.upiId) && (
                        <Button size="sm" className="font-semibold bg-emerald-500 text-black hover:bg-emerald-400" onClick={() => wdPayout(w.id)} data-testid={`wd-payout-${w.id}`}>Pay via IndianPay</Button>
                      )}
                      <Button size="sm" variant={payoutOn && !(w.method === "upi" || w.upiId) ? "outline" : "default"} className="font-semibold" onClick={() => wdAction(w.id, "approve")} data-testid={`wd-approve-${w.id}`}>{(w.method === "upi" || w.upiId) ? "Approve (paid via UPI)" : "Approve (paid by bank)"}</Button>
                      <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={() => wdAction(w.id, "reject")} data-testid={`wd-reject-${w.id}`}>Reject</Button>
                      {w.payoutError && <span className="text-xs text-red-400" data-testid={`wd-payout-error-${w.id}`}>{w.payoutError}</span>}
                    </>
                  ) : w.status === "processing" ? (
                    <>
                      <Badge variant="outline" className="border-amber-500/40 text-amber-400">processing · IndianPay</Badge>
                      <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={() => wdPayout(w.id, true)} data-testid={`wd-payout-recheck-${w.id}`}>Re-check</Button>
                    </>
                  ) : (
                    <Badge variant="outline" className={
                      w.status === "paid" || w.status === "approved" ? "border-primary/40 text-primary" : "border-red-500/40 text-red-400"}>{w.status}{w.utr ? ` · UTR ${w.utr}` : ""}</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Manual bank-transfer deposits */}
        <TabsContent value="deposits" className="mt-5" data-testid="admin-deposits-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-semibold flex items-center gap-2"><Banknote className="h-4 w-4 text-primary" /> Manual deposit requests <span className="text-xs text-muted-foreground font-normal">(bank transfer · +5% bonus on approval)</span></h3>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={mdSearch} onChange={(e) => setMdSearch(e.target.value)} placeholder="Search by UTR or username" className="pl-9" data-testid="admin-md-search" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {["all", "pending", "approved", "rejected"].map((s) => (
              <button key={s} onClick={() => setMdStatus(s)} data-testid={`admin-md-filter-${s}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold capitalize transition-colors ${
                  mdStatus === s ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/70"
                }`}>
                {s} <span className={`ml-1 ${mdStatus === s ? "opacity-80" : "opacity-60"}`}>{mdCounts[s]}</span>
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card divide-y divide-border">
            {filteredMd.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground" data-testid="admin-md-empty">{(mdQuery || mdStatus !== "all") ? "No requests match your filters." : "No deposit requests."}</div>}
            {filteredMd.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={`admin-md-row-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold">{inr(d.amount)} <span className="text-primary text-xs">+ {inr(d.bonus)} bonus</span> <span className="text-xs text-muted-foreground">· {d.username}</span></div>
                  <div className="text-xs text-muted-foreground">{d.method} · UTR {d.utr} · credits {inr(d.total)} · {timeFrom(d.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  {d.status === "pending" ? (
                    <>
                      <Button size="sm" className="font-semibold" onClick={() => mdAction(d.id, "approve")} data-testid={`admin-md-approve-${d.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={() => mdAction(d.id, "reject")} data-testid={`admin-md-reject-${d.id}`}>Reject</Button>
                    </>
                  ) : (
                    <Badge variant="outline" className={d.status === "approved" ? "border-primary/40 text-primary" : "border-red-500/40 text-red-400"}>{d.status}</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-8 mb-4">
            <h3 className="font-display font-semibold flex items-center gap-2"><Banknote className="h-4 w-4 text-primary" /> UPI deposits <span className="text-xs text-muted-foreground font-normal">(IndianPay · auto-verified &amp; credited)</span></h3>
            <span className="text-xs text-muted-foreground" data-testid="admin-upi-count">{upiDeposits.length} orders</span>
          </div>
          <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="admin-upi-list">
            {upiDeposits.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground" data-testid="admin-upi-empty">No UPI deposits yet.</div>}
            {upiDeposits.slice(0, 200).map((d) => (
              <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={`admin-upi-row-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold">{inr(d.amount)} {d.bonus > 0 && <span className="text-primary text-xs">+ {inr(d.bonus)} bonus</span>} <span className="text-xs text-muted-foreground">· {d.username}</span></div>
                  <div className="text-xs text-muted-foreground break-all">{d.client_txn_id}{d.upi_txn_id ? ` · UTR ${d.upi_txn_id}` : ""}{d.customer_vpa ? ` · ${d.customer_vpa}` : ""} · {timeFrom(d.created_at)}{d.remark ? ` · ${d.remark}` : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  {(d.status === "created" || d.status === "mismatch") && (
                    <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={() => upiRecheck(d.id)} data-testid={`admin-upi-recheck-${d.id}`}>Re-check</Button>
                  )}
                  <Badge variant="outline" className={
                    d.status === "success" ? "border-primary/40 text-primary"
                      : d.status === "created" ? "border-amber-500/40 text-amber-400"
                      : d.status === "mismatch" ? "border-orange-500/40 text-orange-400"
                      : "border-red-500/40 text-red-400"}>{d.status === "created" ? "awaiting payment" : d.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="mt-5">
          <AdminUsers token={token} users={users} onChanged={refresh} />
        </TabsContent>

        {/* Audit Log */}
        <TabsContent value="audit" className="mt-5" data-testid="admin-audit-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-semibold flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Money movement audit log <span className="text-xs text-muted-foreground font-normal">(deposits · withdrawals · chip adjustments)</span></h3>
            <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={refresh} data-testid="admin-audit-refresh">Refresh</Button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {[["all", "All"], ["deposits", "Deposits"], ["withdrawals", "Withdrawals"], ["adjust", "Chip Adjust"]].map(([key, label]) => (
              <button key={key} onClick={() => setAuditCat(key)} data-testid={`admin-audit-filter-${key}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  auditCat === key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/70"
                }`}>
                {label} <span className={`ml-1 ${auditCat === key ? "opacity-80" : "opacity-60"}`}>
                  {key === "all" ? auditLogs.length : auditLogs.filter((a) => auditCategory(a.event) === key).length}
                </span>
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card divide-y divide-border">
            {filteredAudit.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground" data-testid="admin-audit-empty">No audit entries yet.</div>}
            {filteredAudit.map((a) => {
              const meta = AUDIT_META[a.event] || { label: a.event, cls: "text-muted-foreground" };
              const positive = (a.amount ?? 0) >= 0;
              return (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={`admin-audit-row-${a.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                      <span className="font-semibold">{a.user_name || "—"}</span>
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">by {a.actor}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">{a.note} · {timeFrom(a.at)}</div>
                  </div>
                  {a.amount != null && (
                    <div className={`font-display font-bold tabular-nums shrink-0 ${a.event === "chip_adjust" ? (positive ? "text-primary" : "text-red-400") : "text-foreground"}`}>
                      {a.event === "chip_adjust" && positive ? "+" : ""}{inr(a.amount)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Support chat */}
        <TabsContent value="support" className="mt-5">
          <AdminChat token={token} />
        </TabsContent>

        {/* Support tickets (chatbot) */}
        <TabsContent value="tickets" className="mt-5" data-testid="admin-tickets-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-semibold flex items-center gap-2"><LifeBuoy className="h-4 w-4 text-primary" /> Support tickets <span className="text-xs text-muted-foreground font-normal">(raised via the chat bot)</span></h3>
            <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={refresh} data-testid="admin-tickets-refresh">Refresh</Button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {[["pending", "Open"], ["all", "All"]].map(([key, label]) => (
              <button key={key} onClick={() => setTicketFilter(key)} data-testid={`admin-ticket-filter-${key}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  ticketFilter === key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/70"
                }`}>
                {label} <span className={`ml-1 ${ticketFilter === key ? "opacity-80" : "opacity-60"}`}>
                  {key === "all" ? tickets.length : tickets.filter((t) => t.status === key).length}
                </span>
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card divide-y divide-border">
            {filteredTickets.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground" data-testid="admin-tickets-empty">No {ticketFilter === "all" ? "" : ticketFilter} tickets.</div>}
            {filteredTickets.map((t) => (
              <div key={t.id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-4" data-testid={`admin-ticket-${t.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="border-primary/40 text-primary">{t.category}</Badge>
                    <span className="font-semibold">{t.username || t.userName || "—"}</span>
                    <span className="text-xs text-muted-foreground">· {t.ref}</span>
                    <Badge variant="outline" className={t.status === "resolved" ? "border-primary/40 text-primary" : "border-amber-500/40 text-amber-400"}>{t.status}</Badge>
                  </div>
                  <div className="text-sm text-foreground mt-1.5 whitespace-pre-wrap break-words">{t.details}</div>
                  <div className="text-xs text-muted-foreground mt-1">{timeFrom(t.created_at)}</div>
                  {(t.comments || []).length > 0 && (
                    <div className="mt-2 space-y-1" data-testid={`admin-ticket-comments-${t.id}`}>
                      {t.comments.map((c, i) => (
                        <div key={i} className="text-xs rounded-lg bg-primary/10 border border-primary/20 px-2.5 py-1.5"><span className="text-primary font-semibold">You · {timeFrom(c.at)}</span> — {c.text}</div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <input value={ticketNote[t.id] || ""} onChange={(e) => setTicketNote((p) => ({ ...p, [t.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") commentTicket(t.id); }}
                      placeholder="Comment to user…" data-testid={`admin-ticket-comment-input-${t.id}`}
                      className="flex-1 min-w-[180px] rounded-full bg-input/50 border border-input px-3.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-ring" />
                    <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={() => commentTicket(t.id)} disabled={!(ticketNote[t.id] || "").trim()} data-testid={`admin-ticket-comment-send-${t.id}`}>Send</Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" className="font-semibold" onClick={() => resolveTicket(t.id)} data-testid={`admin-ticket-resolve-${t.id}`}>Resolve & Close</Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings" className="mt-5 space-y-5">
          <AdminCasinoStatus token={token} />
          <div className="rounded-2xl border border-border bg-card p-6 max-w-lg space-y-4" data-testid="admin-bank-editor">
            <h3 className="font-display font-semibold flex items-center gap-2"><Banknote className="h-4 w-4 text-primary" /> Deposit Bank Account</h3>
            <p className="text-xs text-muted-foreground">These details are shown to all users (read-only) on the Bank Transfer deposit tab. Only admins can edit them here.</p>
            <div><Label>Account holder name</Label><Input value={bank.accountName} onChange={(e) => setBank({ ...bank, accountName: e.target.value })} placeholder="e.g. BatBallWorld Payments Pvt Ltd" className="mt-1.5" data-testid="admin-bank-name" /></div>
            <div><Label>Account number</Label><Input value={bank.accountNumber} onChange={(e) => setBank({ ...bank, accountNumber: e.target.value.replace(/\D/g, "") })} placeholder="e.g. 502000123456789" className="mt-1.5" data-testid="admin-bank-number" /></div>
            <div><Label>IFSC code</Label><Input value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase().slice(0, 11) })} placeholder="e.g. HDFC0001234" className="mt-1.5 uppercase" data-testid="admin-bank-ifsc" /></div>
            <div><Label>Bank name (optional)</Label><Input value={bank.bankName} onChange={(e) => setBank({ ...bank, bankName: e.target.value })} placeholder="e.g. HDFC Bank" className="mt-1.5" data-testid="admin-bank-bankname" /></div>
            <Button className="font-semibold" onClick={saveBank} disabled={bankSaving} data-testid="admin-bank-save">{bankSaving ? "Saving…" : "Save Bank Details"}</Button>
          </div>
          <AdminManualPayMethods token={token} />
        </TabsContent>

        {/* House P&L (per-day, casino vs cricket) */}
        <TabsContent value="house" className="mt-5" data-testid="admin-house-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-semibold flex items-center gap-2"><LineChart className="h-4 w-4 text-primary" /> House P&amp;L <span className="text-xs text-muted-foreground font-normal">(daily · last 90 days · positive = house profit)</span></h3>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={refresh} data-testid="admin-house-refresh">Refresh</Button>
              <Button size="sm" variant="outline" className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 hover:text-red-300" onClick={resetHouse} data-testid="admin-house-reset">Reset P&amp;L</Button>
            </div>
          </div>
          {houseReport?.houseResetAt && (
            <p className="text-xs text-muted-foreground -mt-2 mb-4" data-testid="house-reset-note">
              Counting since <span className="text-foreground font-medium">{new Date(houseReport.houseResetAt).toLocaleString("en-IN")}</span>
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            {[["Casino games", houseReport?.totals?.casino], ["Cricket bets", houseReport?.totals?.cricket], ["Total", houseReport?.totals?.total]].map(([label, val]) => (
              <div key={label} className="rounded-2xl border border-border bg-card p-4" data-testid={`house-total-${label.split(" ")[0].toLowerCase()}`}>
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className={`mt-1 font-display text-2xl font-bold ${(val ?? 0) >= 0 ? "text-primary" : "text-red-400"}`}>
                  {(val ?? 0) >= 0 ? "+" : "−"}{inr(Math.abs(val ?? 0))}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="grid grid-cols-4 gap-2 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border">
              <span>Date</span>
              <span className="text-right">Casino</span>
              <span className="text-right">Cricket</span>
              <span className="text-right">Total</span>
            </div>
            {(!houseReport || houseReport.days.length === 0) && (
              <div className="px-4 py-8 text-center text-muted-foreground" data-testid="house-empty">No activity yet.</div>
            )}
            {houseReport?.days?.map((d) => (
              <div key={d.date} className="grid grid-cols-4 gap-2 px-4 py-3 text-sm border-b border-border/50 last:border-0" data-testid={`house-row-${d.date}`}>
                <span className="font-medium">{new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                <span className={`text-right ${d.casino >= 0 ? "text-primary" : "text-red-400"}`}>
                  {d.casino >= 0 ? "+" : "−"}{inr(Math.abs(d.casino))}
                  <span className="block text-[10px] text-muted-foreground">{d.casinoPlays} plays</span>
                </span>
                <span className={`text-right ${d.cricket >= 0 ? "text-primary" : "text-red-400"}`}>
                  {d.cricket >= 0 ? "+" : "−"}{inr(Math.abs(d.cricket))}
                  <span className="block text-[10px] text-muted-foreground">{d.cricketPlays} bets</span>
                </span>
                <span className={`text-right font-semibold ${d.total >= 0 ? "text-primary" : "text-red-400"}`}>
                  {d.total >= 0 ? "+" : "−"}{inr(Math.abs(d.total))}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Affiliates */}
        <TabsContent value="affiliates" className="mt-5" data-testid="admin-affiliates-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Affiliates <span className="text-xs text-muted-foreground font-normal">(10% of counted members' net losses · blocks of 5)</span></h3>
            <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={refresh} data-testid="admin-affiliates-refresh">Refresh</Button>
          </div>
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border">
              <span className="col-span-4">Affiliate</span>
              <span className="col-span-2 text-center">Members</span>
              <span className="col-span-2 text-right">Pending</span>
              <span className="col-span-2 text-right">Paid</span>
              <span className="col-span-2 text-right">Action</span>
            </div>
            {affiliates.length === 0 && (
              <div className="px-4 py-8 text-center text-muted-foreground" data-testid="affiliates-empty">No affiliate accounts yet.</div>
            )}
            {affiliates.map((a) => (
              <div key={a.id} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm border-b border-border/50 last:border-0 items-center" data-testid={`affiliate-row-${a.id}`}>
                <div className="col-span-4 min-w-0">
                  <div className="font-medium truncate">{a.name || a.username}</div>
                  <div className="text-[11px] text-muted-foreground">@{a.username} · <span className="font-mono">{a.affiliateCode}</span></div>
                </div>
                <div className="col-span-2 text-center">
                  <span className="font-medium">{a.countedMembers}</span>
                  <span className="text-[11px] text-muted-foreground">/{a.totalMembers}</span>
                </div>
                <div className="col-span-2 text-right font-semibold text-primary">{inr(a.pending)}</div>
                <div className="col-span-2 text-right text-muted-foreground">{inr(a.paid)}</div>
                <div className="col-span-2 text-right">
                  <Button size="sm" className="font-semibold" disabled={a.pending <= 0} onClick={() => payAffiliate(a)} data-testid={`affiliate-pay-${a.id}`}>
                    {a.pending > 0 ? "Pay" : "Paid"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
}
